import React from 'react';

/** Lightweight markdown -> React element formatter (subset) */
export function formatMarkdownToElements(raw: string): React.ReactNode[] {
  if (!raw) return [];
  // Normalize newlines and also convert escaped "\n" sequences into real newlines (common when text stored with escapes)
  let normalized = raw.replace(/\r\n?/g, '\n');
  if (normalized.includes('\\n')) {
    normalized = normalized.replace(/\\n/g, '\n');
  }
  // If it doesn't look like markdown (no headings, lists, code fences, blockquotes) just render as preformatted text
  const looksLikeMarkdown = /(^|\n)(#{1,6}\s|[-*+]\s|\d+\.\s|```|>\s?)/.test(normalized);
  if (!looksLikeMarkdown) {
    return [
      <pre
        key={0}
        className="text-sm leading-relaxed text-gray-200 whitespace-pre-wrap break-words font-mono"
      >
        {normalized}
      </pre>
    ];
  }
  const lines = normalized.split('\n');
  const out: React.ReactNode[] = [];
  let i = 0;
  let key = 0;
  let inCode = false;
  let codeLang = '';
  let codeBuffer: string[] = [];

  const flushCode = () => {
    if (codeBuffer.length) {
      out.push(
        <pre key={key++} className="mb-4 p-3 rounded bg-black/70 overflow-auto">
          <code data-lang={codeLang} className="text-sm leading-relaxed text-gray-100 whitespace-pre">
            {codeBuffer.join('\n')}
          </code>
        </pre>
      );
      codeBuffer = [];
      codeLang = '';
    }
  };

  const parseInline = (text: string): React.ReactNode[] => {
    const parts: React.ReactNode[] = [];
    let remaining = text;
    const pushText = (t: string) => { if (t) parts.push(t); };
    while (remaining.length) {
      const codeIdx = remaining.indexOf('`');
      if (codeIdx === -1) { pushText(remaining); break; }
      // text before tick
      pushText(remaining.slice(0, codeIdx));
      const rest = remaining.slice(codeIdx + 1);
      const end = rest.indexOf('`');
      if (end === -1) { // unmatched – treat as literal
        pushText('`');
        remaining = rest;
        continue;
      }
      const codeContent = rest.slice(0, end);
      parts.push(<code key={parts.length} className="px-1.5 py-0.5 rounded bg-gray-800 text-amber-200">{codeContent}</code>);
      remaining = rest.slice(end + 1);
    }
    // Bold & italic (** or *) simple replacements afterwards
    return parts.map((p, index) => typeof p === 'string' ? <React.Fragment key={index}>{renderEmphasis(p)}</React.Fragment> : p);
  };

  const renderEmphasis = (segment: string): React.ReactNode => {
    // Bold **text** then italic *text*
    const boldPattern = /\*\*(.+?)\*\*/g;
    const italicPattern = /\*(?!\*)(.+?)\*/g;
    let nodes: React.ReactNode[] = [];
    let lastIndex = 0; let m: RegExpExecArray | null;
    const pushPlain = (s: string) => { if (s) nodes.push(s); };
    while ((m = boldPattern.exec(segment))) {
      pushPlain(segment.slice(lastIndex, m.index));
      nodes.push(<strong key={nodes.length}>{m[1]}</strong>);
      lastIndex = m.index + m[0].length;
    }
    pushPlain(segment.slice(lastIndex));
    // For each string node, further split italics
    nodes = nodes.flatMap((n, nodeIndex) => {
      if (typeof n !== 'string') return [n];
      const parts: React.ReactNode[] = [];
      let li = 0; let im: RegExpExecArray | null; italicPattern.lastIndex = 0;
      while ((im = italicPattern.exec(n))) {
        const before = n.slice(li, im.index); if (before) parts.push(before);
        parts.push(<em key={`${nodeIndex}-${parts.length}`}>{im[1]}</em>);
        li = im.index + im[0].length;
      }
      const tail = n.slice(li); if (tail) parts.push(tail);
      return parts;
    });
    return <>{nodes.map((node, index) => <React.Fragment key={index}>{node}</React.Fragment>)}</>;
  };

  const collectList = (start: number): [React.ReactNode, number] => {
    const isOrdered = /^\d+\./.test(lines[start].trim());
    const items: React.ReactNode[] = [];
    let idx = start;
    while (idx < lines.length) {
      const trimmed = lines[idx].trim();
      if (!trimmed) break;
      if (isOrdered) {
        if (!/^\d+\./.test(trimmed)) break;
      } else {
        if (!/^[-*+]\s+/.test(trimmed)) break;
      }
      const content = trimmed.replace(/^([-*+]|\d+\.)\s+/, '');
      items.push(<li key={items.length} className="leading-relaxed">{parseInline(content)}</li>);
      idx++;
    }
    const listEl = isOrdered ? <ol key={key++} className="list-decimal ml-6 mb-3 space-y-1 text-gray-200">{items}</ol>
                              : <ul key={key++} className="list-disc ml-6 mb-3 space-y-1 text-gray-200">{items}</ul>;
    return [listEl, idx - 1];
  };

  for (i = 0; i < lines.length; i++) {
    const line = lines[i];
    const fenceMatch = line.match(/^```(.*)$/);
    if (fenceMatch) {
      if (inCode) {
        flushCode();
        inCode = false;
      } else {
        inCode = true;
        codeLang = fenceMatch[1].trim();
      }
      continue;
    }
    if (inCode) { codeBuffer.push(line); continue; }
    const trimmed = line.trim();
    // Preserve blank lines as explicit line breaks so original newlines are visible
    if (!trimmed) { 
      out.push(<br key={key++} />);
      continue; 
    }
    if (/^#{1,6}\s+/.test(trimmed)) {
      const level = trimmed.match(/^#{1,6}/)![0].length;
      const text = trimmed.replace(/^#{1,6}\s+/, '');
      const Tag = `h${Math.min(level,6)}` as keyof JSX.IntrinsicElements;
      const headingSizeClass = (
        level === 1 ? 'text-2xl' :
        level === 2 ? 'text-xl' :
        level === 3 ? 'text-lg' : 'text-base'
      );
      out.push(
        React.createElement(
          Tag,
          { key: key++, className: `mt-4 mb-2 font-semibold text-gray-100 ${headingSizeClass}` },
          parseInline(text)
        )
      );
      continue;
    }
    if (/^[-*+]\s+/.test(trimmed) || /^\d+\./.test(trimmed)) {
      const [listEl, newIndex] = collectList(i);
      out.push(listEl);
      i = newIndex;
      continue;
    }
    if (/^>\s?/.test(trimmed)) {
      const quoteLines: string[] = [];
      let j = i;
      while (j < lines.length && /^>\s?/.test(lines[j].trim())) {
        quoteLines.push(lines[j].trim().replace(/^>\s?/, ''));
        j++;
      }
      out.push(<blockquote key={key++} className="border-l-4 border-indigo-400 pl-4 italic text-indigo-200 my-4">{formatMarkdownToElements(quoteLines.join('\n')).map((n, idx)=> <React.Fragment key={idx}>{n}</React.Fragment>)}</blockquote>);
      i = j - 1; continue;
    }
    if (/^---+$/.test(trimmed)) {
      out.push(<hr key={key++} className="my-6 border-gray-700" />); continue;
    }
    // Paragraph
    out.push(<p key={key++} className="mb-3 leading-relaxed text-gray-200">{parseInline(trimmed)}</p>);
  }
  if (inCode) flushCode();
  return out;
}
